import { Component, Input } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { interval, of, take, throwError } from 'rxjs';
import { catchError, finalize, map, switchMap, tap } from 'rxjs/operators';
import { SingleApplicationDto } from '@dto';
import { Credentials } from '@model';
import {
  ApplicationService,
  CookieService,
  GlobalAlertService,
  LoginService,
  PasswordService,
  UserService,
} from '@service';

@Component({
  selector: 'login-credentials-form',
  // eslint-disable-next-line @angular-eslint/prefer-standalone
  standalone: false,
  styleUrls: ['./credentials-form.component.scss'],
  templateUrl: './credentials-form.component.html',
})
export class CredentialsFormComponent {
  passwordShown = false;
  oldPasswordShown = false;
  resettingPassword = false;
  loggedIn = false;
  apps: SingleApplicationDto[];
  loggingIn = false;

  @Input() loginForm: FormGroup;

  constructor(
    private loginService: LoginService,
    private passwordService: PasswordService,
    private cookieService: CookieService,
    private globalAlertService: GlobalAlertService,
    private userService: UserService,
    private applicationService: ApplicationService,
  ) {
    this.apps = [];
  }

  get isAdmin() {
    return this.userService.isAdmin;
  }

  get credentials(): Credentials {
    return { ...this.loginForm.getRawValue() } as Credentials;
  }

  get username(): string {
    return this.loginForm.get('username').value;
  }

  get passwordField() {
    return this.loginForm.get('password');
  }

  get oldPasswordField() {
    return this.loginForm.get('oldPassword');
  }

  get oldPassword(): string {
    return this.loginForm.get('oldPassword').value;
  }

  set oldPassword(password: string) {
    this.loginForm.get('oldPassword').setValue(password);
  }

  get password(): string {
    return this.loginForm.get('password').value;
  }

  toggleShowPassword() {
    this.passwordShown = !this.passwordShown;
  }

  toggleShowOldPassword() {
    this.oldPasswordShown = !this.oldPasswordShown;
  }

  toggleResetPassword() {
    this.resettingPassword = !this.resettingPassword;
    if (!this.resettingPassword) {
      this.oldPassword = null;
    }
  }

  login() {
    this.loggingIn = true;
    this.cookieService.deleteCookie('des_session');
    if (this.resettingPassword) {
      this.resettingPassword = false;
    }
    this.loginService
      .login(this.credentials)
      .pipe(
        catchError(error => {
          console.error('Error caught logging in: ', error);
          // Handle the error appropriately, maybe rethrow or return a default value
          this.globalAlertService.setError('Login failed. Please try again.', 10000);
          this.loggingIn = false;
          return throwError(() => error);
        }),
        tap(() => {
          if (!this.loginService.hasRedirect()) {
            this.globalAlertService.setSuccess('Login Successful...', 10000);
            this.loggedIn = true;
          }
        }),
        switchMap(() =>
          this.loginService.hasRedirect()
            ? of(null) // If there's a redirect, skip further processing
            : this.loginService.handleLogin(),
        ),
        switchMap(user =>
          user
            ? this.applicationService.getApplications().pipe(
                map(apps => {
                  const filteredApps = apps.filter(app => app.shownInNav !== false);
                  return { apps: filteredApps, user };
                }),
              )
            : of(null),
        ),
      )
      .subscribe(result => {
        if (!result) {
          // Handle redirect if applicable
          this.loginService.redirect();
          this.loggingIn = false;
          return;
        }

        const { apps, user } = result;
        this.apps = apps;

        const matchedApp = this.apps.find(app => user?.homeApp?.toUpperCase() === app.id);

        if (this.apps?.length > 0) {
          const targetApp = matchedApp ?? (this.apps[0].id !== 'LOGOUT' ? this.apps[0] : this.apps[1]);
          this.globalAlertService.setSuccess(`Opening ${targetApp.displayLabel}`, 10000);
          this.countDownToNavigate(targetApp.url);
        } else if (!this.userService.isAdmin) {
          this.globalAlertService.setError('No home application configured...You are now officially stranded.', 10000);
        }
        this.loggingIn = false;
      });
  }

  countDownToNavigate(url: string): void {
    const seconds = 5;

    interval(1000) // Emit values every 1 second
      .pipe(
        take(seconds), // Complete after 5 emissions
        map(count => seconds - count - 1), // Map emitted value to remaining seconds
        finalize(() => {
          window.location.href = url; // Navigate once the countdown is complete
        }),
      )
      .subscribe(remaining => {
        console.log(`Navigating in ${remaining} seconds...`);
      });
  }

  resetPassword() {
    if (!this.resettingPassword) {
      return;
    }
    this.passwordService.resetPassword(this.credentials).subscribe(() => {
      this.globalAlertService.setSuccess('Successfully reset password...');
      this.toggleResetPassword();
    });
  }

  passwordValid() {
    return this.password && this.oldPassword && this.username;
  }

  goTo(url: string) {
    window.location.href = url;
  }
}
